import Stack from "@mui/material/Stack";
import { styled, useColorScheme } from "@mui/material/styles";
import { memo, useMemo } from "react";
import { Link, useInRouterContext } from "react-router-dom";

import { routers } from "@/configs";
import { useDeviceType, useTranslate } from "@/hooks";
import { useSelector } from "@/store";

import {
  LOGO_DEFAULT_SRC,
  LOGO_FULL_SIZE,
  LOGO_FULL_SX,
  LOGO_LIGHT_SRC,
  LOGO_MOBILE_DEFAULT_SRC,
  LOGO_MOBILE_LIGHT_SRC,
  LOGO_MOBILE_SIZE,
  LOGO_MOBILE_SX,
} from "./data";

// Not Box: Box reads width/height as style props, and the img needs them as attributes.
const LogoImage = styled("img")({});

interface LogoProps {
  fullWidth?: boolean;
  on?: "light" | "dark";
}

const Logo = ({ fullWidth, on }: LogoProps) => {
  const { isMobile, isTablet } = useDeviceType();
  const t = useTranslate();
  // The auth and wallet dialogs mount beside the router, not inside it, and a
  // router link there has no router to speak to.
  const inRouter = useInRouterContext();
  const data = useSelector((state) => state.app.bootstrap).data;
  const { mode, systemMode } = useColorScheme();
  const resolvedMode = on ?? ((systemMode || mode || "dark") as "light" | "dark");

  const isLight = resolvedMode === "light";

  const logoProps = useMemo(() => {
    if (fullWidth || (!isMobile && !isTablet)) {
      return {
        src: isLight ? LOGO_LIGHT_SRC : LOGO_DEFAULT_SRC,
        sx: LOGO_FULL_SX,
        size: LOGO_FULL_SIZE,
        alt: data?.name || "Company Logo",
      };
    }

    return {
      src: isLight ? LOGO_MOBILE_LIGHT_SRC : LOGO_MOBILE_DEFAULT_SRC,
      sx: LOGO_MOBILE_SX,
      size: LOGO_MOBILE_SIZE,
      alt: data?.name || "Mobile Company Logo",
    };
  }, [isMobile, isTablet, fullWidth, data?.name, isLight]);

  const image = (
    <LogoImage
      src={logoProps.src}
      alt={logoProps.alt}
      sx={logoProps.sx}
      width={logoProps.size.width}
      height={logoProps.size.height}
      loading="eager"
      decoding="sync"
    />
  );

  // In-app navigation where there is a router: as an <a href> it reloaded the
  // whole site, through the health check, the socket and the chat history.
  return inRouter ? (
    <Stack component={Link} to={routers.Home} aria-label={t("common.home")}>
      {image}
    </Stack>
  ) : (
    <Stack component="a" href={routers.Home} aria-label={t("common.home")}>
      {image}
    </Stack>
  );
};

export default memo(Logo);
